import { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Breadcrumbs, Link as MuiLink, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, TextField, Tooltip, LinearProgress, Button,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import RuleIcon from '@mui/icons-material/Rule';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { getStandardControls } from '../../../services/securityhubApi';
import { COUNTED_FINDINGS_QUERY } from './ComplianceDashboard';
import { standardLabel } from './standards';
import { severityChipSx } from './severity';
import { formatStatusLabel } from '../../../utils/statusLabel';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import {
  statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  sectionCardSx, sectionHeadingSx, emptyTextSx, actionRowSx,
} from '../portalStyles';

// countByStatus tallies control rows by controlStatus.
const countByStatus = (rows) => rows.reduce(
  (a, c) => { a[c.controlStatus] = (a[c.controlStatus] || 0) + 1; return a; },
  {},
);

const STATUS_COLOR = { PASSED: 'success', FAILED: 'error', UNKNOWN: 'warning', NO_DATA: 'default' };
const STATUS_LABEL = { PASSED: 'Passed', FAILED: 'Failed', UNKNOWN: 'Unknown', NO_DATA: 'No data' };
const FILTERS = ['', 'FAILED', 'PASSED', 'UNKNOWN', 'NO_DATA'];
const FILTER_LABEL = { '': 'All', FAILED: 'Failed', PASSED: 'Passed', UNKNOWN: 'Unknown', NO_DATA: 'No data' };

export default function StandardDetail() {
  const { standardId } = useParams();
  const navigate = useNavigate();
  const decoded = decodeURIComponent(standardId);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  // Per-standard control totals for the summary card. `null` until they land —
  // never an empty object, so "still loading" and "genuinely zero" stay
  // distinguishable and the card can't flash four confident zeroes.
  const [totals, setTotals] = useState(null);
  const [totalsFailed, setTotalsFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStandardControls(decoded, { status, search });
      setControls(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [decoded, status, search]);
  useEffect(() => { load(); }, [load]);

  // The "Control status" card describes THE STANDARD, so it is read from its own
  // UNFILTERED request rather than from `controls`, which is whatever the current
  // status/search filter returned. Reducing the filtered rows made the card move
  // with the filter — pick Failed and it read "0 Passed / N Failed / 0 Unknown /
  // 0 No data" — so the same four chips meant different things depending on state
  // the card never showed.
  //
  // Keyed on the standard alone: filtering or typing in the search box does not
  // re-request it, so this costs ONE extra call per standard opened. The API
  // returns control rows and no per-status counts, so there is nothing cheaper to
  // read; ControlDetail already fetches the same unfiltered list the same way.
  useEffect(() => {
    let ignore = false;
    setTotals(null);
    setTotalsFailed(false);
    getStandardControls(decoded, {})
      .then((res) => { if (!ignore) setTotals(countByStatus(res?.data || [])); })
      .catch(() => { if (!ignore) setTotalsFailed(true); });
    return () => { ignore = true; };
  }, [decoded]);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/securityhub/cspm" underline="hover">Security Hub CSPM</MuiLink>
        <MuiLink component={RouterLink} to="/securityhub/cspm/standards" underline="hover">Security standards</MuiLink>
        <Typography color="text.primary">{standardLabel(decoded)}</Typography>
      </Breadcrumbs>
      <PortalPageHeader
        icon={<SecurityIcon sx={{ fontSize: 24 }} />}
        title={standardLabel(decoded)}
        subtitle="Control-by-control results for this Security Hub standard"
        action={(
          /* The only way into the findings list scoped to one standard. The
             list has always accepted ?standard= and has always drawn a
             removable "Standard: …" chip for it, but nothing navigated with
             it, so both were unreachable.

             COUNTED_FINDINGS_QUERY rides along for the same reason the severity
             and per-account links carry it: the list would otherwise open on
             passing checks and on findings this operator already suppressed or
             resolved, and this console's numbers would stop agreeing with each
             other across two clicks. */
          <Button
            variant="outlined"
            startIcon={<FactCheckIcon />}
            onClick={() => navigate(
              `/securityhub/cspm/findings?standard=${encodeURIComponent(decoded)}&${COUNTED_FINDINGS_QUERY}`,
            )}
          >
            View failing findings
          </Button>
        )}
      />
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={load} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 0.5 }}>Control status</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Every control in this standard — unaffected by the filters below.
        </Typography>
        {totals ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Chip label={`${totals.PASSED || 0} Passed`} size="small" sx={statusChipSx('success')} />
            <Chip label={`${totals.FAILED || 0} Failed`} size="small" sx={statusChipSx('error')} />
            <Chip label={`${totals.UNKNOWN || 0} Unknown`} size="small" sx={statusChipSx('warning')} />
            <Chip label={`${totals.NO_DATA || 0} No data`} size="small" sx={statusChipSx('neutral')} />
          </Box>
        ) : (
          <Typography sx={emptyTextSx}>
            {totalsFailed
              ? "Couldn't load this standard's control totals — reload the page to try again."
              : 'Counting controls…'}
          </Typography>
        )}
      </Paper>
      </motion.div>

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<RuleIcon sx={{ fontSize: 20 }} />}
          title={`All controls (${controls.length})`}
          subtitle="Filter by status or search a control by ID or title"
          mb={2}
        />
        <Box sx={{ ...actionRowSx, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {FILTERS.map((f) => (
              <Chip
                key={f || 'all'}
                label={FILTER_LABEL[f]}
                size="small"
                onClick={() => setStatus(f)}
                sx={{
                  ...statusChipSx(status === f ? 'info' : 'neutral'),
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease',
                }}
              />
            ))}
          </Box>
          <TextField size="small" label="Search controls" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 0, flex: '1 1 220px', maxWidth: 340 }} />
        </Box>
        {loading && controls.length > 0 && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}
        {loading && controls.length === 0 ? (
          <PortalLoading label="Loading controls…" />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>ID</TableCell>
                  <TableCell sx={headCellSx}>Title</TableCell>
                  <TableCell sx={headCellSx}>Control status</TableCell>
                  <TableCell sx={headCellSx}>Severity</TableCell>
                  <TableCell sx={headCellSx}>Failed checks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {controls.map((c, i) => (
                  <TableRow key={c.securityControlId} hover sx={{ ...rowSx(i), cursor: 'pointer' }}
                    onClick={() => navigate(`/securityhub/cspm/standards/${encodeURIComponent(decoded)}/controls/${encodeURIComponent(c.securityControlId)}`)}>
                    <TableCell sx={bodyCellSx}>{c.securityControlId}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, maxWidth: 460, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={c.title || ''}>
                        <span>{c.title}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={bodyCellSx}><Chip size="small" label={STATUS_LABEL[c.controlStatus] || formatStatusLabel(c.controlStatus)} sx={statusChipSx(intentFromColor(STATUS_COLOR[c.controlStatus] || 'default'))} /></TableCell>
                    <TableCell sx={bodyCellSx}><Chip size="small" label={c.severityRating} sx={severityChipSx(c.severityRating)} /></TableCell>
                    <TableCell sx={bodyCellSx}>{c.failedChecks} of {c.totalChecks}</TableCell>
                  </TableRow>
                ))}
                {!loading && controls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>
                        No controls match this filter — clear the search or pick another status.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      </motion.div>
    </motion.div>
  );
}
